import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Snackbar,
  Typography,
} from '@mui/material';
import { UserContext } from '../components/MiniDrawer';
import DayNavigator from '../components/daily/DayNavigator';
import PageHeader, { PageContainer } from '../components/PageHeader';
import AbsenceMenu from '../components/daily/AbsenceMenu';
import AbsenceDaysDialog from '../components/daily/AbsenceDaysDialog';
import EntryCard from '../components/daily/EntryCard';
import DayWorklogTable from '../components/daily/DayWorklogTable';
import WorklogEditDialog from '../components/daily/WorklogEditDialog';
import DayProjectsCard from '../components/daily/DayProjectsCard';
import MonthCalendar from '../components/home/MonthCalendar';
import { MONTH_NAMES } from '../components/monthly/format';
import { useHomeMonth } from '../hooks/useHomeMonth';
import {
  createWorklog,
  deleteWorklog,
  fetchAllowedActivityIds,
  fetchUserProjects,
  fetchWorklogOptions,
  fetchWorklogsInRange,
  updateWorklog,
} from '../utils/worklogService';
import {
  cleanTicketId,
  DAILY_WARNING_MINUTES,
  filterProjectsForDate,
  getDateLockMessage,
  getProjectBlockMessage,
  getTicketPrefix,
  isDateBlockedForProject,
  isDateLocked,
  validateWorklog,
} from '../utils/worklogRules';
import { formatMinutes, MAX_DAILY_MINUTES, parseEffort } from '../utils/effortParser';
import { formatDateYYYYMMDD, parseLocalDate } from '../utils/dateHelpers';
import { addAbsences, deleteAbsence } from '../utils/absenceService';
import { invalidatePeopleMonth } from '../utils/monthlyPeopleService';

const DEFAULT_EFFORT_TYPE_ID = 1;
let rowSeq = 0;
const newRow = (ticket = '') => ({ key: `r${++rowSeq}`, ticket, effort: '', description: '' });
const noErrors = () => ({ common: {}, rows: {}, general: null });

export default function DailyTrackingV2() {
  const { selectedUser } = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [date, setDate] = useState(() => (searchParams.get('date') ? parseLocalDate(searchParams.get('date')) : new Date()));
  const dateKey = formatDateYYYYMMDD(date);
  const dayLabel = date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  const userId = selectedUser && selectedUser !== 'ALL' ? selectedUser : null;
  const yearMonth = dateKey.slice(0, 7);

  const [activities, setActivities] = useState([]);
  const [effortTypes, setEffortTypes] = useState([]);
  const [userProjects, setUserProjects] = useState([]);
  const [allowedMap, setAllowedMap] = useState({});
  const [optionsError, setOptionsError] = useState(null);

  const [worklogs, setWorklogs] = useState([]);
  const [dayLoading, setDayLoading] = useState(false);
  const [dayError, setDayError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const [common, setCommon] = useState({ projectId: '', activityId: '', effortTypeId: '' });
  const [rows, setRows] = useState(() => [newRow()]);
  const [errors, setErrors] = useState(noErrors);
  const [saving, setSaving] = useState(false);
  const [confirmOver, setConfirmOver] = useState(null);
  const [snack, setSnack] = useState(null);

  const [editing, setEditing] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [absenceDaysOpen, setAbsenceDaysOpen] = useState(false);

  const month = useHomeMonth(userId, date.getFullYear(), date.getMonth() + 1, refreshToken);

  // ---- Opzioni ----
  useEffect(() => {
    fetchWorklogOptions()
      .then(({ activities: a, effortTypes: et }) => {
        setActivities(a);
        setEffortTypes(et);
        const def = et.find((t) => Number(t.id) === DEFAULT_EFFORT_TYPE_ID) || et[0];
        if (def) setCommon((c) => (c.effortTypeId ? c : { ...c, effortTypeId: def.id }));
      })
      .catch((e) => setOptionsError(`Unable to load activities: ${e.message}`));
  }, []);

  useEffect(() => {
    if (!userId) return;
    fetchUserProjects(userId)
      .then(setUserProjects)
      .catch((e) => setOptionsError(`Unable to load your projects: ${e.message}`));
  }, [userId]);

  const ensureAllowed = useCallback(
    (projectId) => {
      if (!projectId || allowedMap[projectId] !== undefined) return;
      setAllowedMap((m) => ({ ...m, [projectId]: null }));
      fetchAllowedActivityIds(projectId).then((ids) => setAllowedMap((m) => ({ ...m, [projectId]: ids })));
    },
    [allowedMap],
  );
  const activitiesFor = useCallback(
    (projectId) => {
      const allowed = allowedMap[projectId];
      return allowed && allowed.length ? activities.filter((a) => allowed.includes(a.id)) : activities;
    },
    [activities, allowedMap],
  );
  const findProject = useCallback((id) => userProjects.find((p) => p.id === id) || null, [userProjects]);
  const findActivity = useCallback((id) => activities.find((a) => a.id === id) || null, [activities]);

  // ---- Worklog del giorno ----
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    setDayLoading(true);
    setDayError(null);
    fetchWorklogsInRange(userId, dateKey, dateKey)
      .then((list) => !cancelled && setWorklogs(list.sort((a, b) => String(a.created_at || a.id).localeCompare(String(b.created_at || b.id)))))
      .catch((e) => !cancelled && setDayError(`Unable to load worklogs: ${e.message}`))
      .finally(() => !cancelled && setDayLoading(false));
    return () => {
      cancelled = true;
    };
  }, [userId, dateKey, refreshToken]);

  const refresh = () => {
    if (userId) invalidatePeopleMonth([userId], yearMonth);
    setRefreshToken((t) => t + 1);
  };

  // ---- Giorno ----
  const changeDate = (d) => {
    setDate(d);
    setErrors(noErrors());
    setSearchParams({ date: formatDateYYYYMMDD(d) }, { replace: true });
  };

  const projectsForDate = useMemo(() => filterProjectsForDate(userProjects, date), [userProjects, date]);
  const project = findProject(common.projectId);
  const activity = findActivity(common.activityId);
  const prefix = getTicketPrefix(project, activity);
  const locked = !userId || isDateLocked(date);
  const lockMessage = !userId ? 'Select a user to track hours.' : getDateLockMessage(date);
  const savedMinutes = worklogs.reduce((sum, w) => sum + (Number(w.effort) || 0), 0);
  const pendingMinutes = rows.reduce((sum, r) => {
    const p = parseEffort(r.effort);
    return sum + (p.valid ? p.minutes : 0);
  }, 0);
  const dayInfo = (month.days || []).find((d) => d.dateKey === dateKey);
  const absence = dayInfo?.absence || null;
  const blockMessage = project && isDateBlockedForProject(date, project) ? getProjectBlockMessage(date, project) : '';

  // ---- Form ----
  const changeCommon = (patch) => {
    const next = { ...common, ...patch };
    if (patch.projectId) ensureAllowed(patch.projectId);
    const allowed = allowedMap[next.projectId];
    if (allowed && allowed.length && next.activityId && !allowed.includes(next.activityId)) next.activityId = '';
    // Prefisso ticket coerente su tutte le righe
    const oldPrefix = prefix;
    const newPrefix = getTicketPrefix(findProject(next.projectId), findActivity(next.activityId));
    if (newPrefix !== oldPrefix) {
      setRows((rs) =>
        rs.map((r) => {
          const t = r.ticket.trim();
          if (!t || t === oldPrefix) return { ...r, ticket: newPrefix };
          if (oldPrefix && t.startsWith(oldPrefix)) return { ...r, ticket: newPrefix + t.slice(oldPrefix.length) };
          return r;
        }),
      );
    }
    setCommon(next);
    setErrors((e) => ({ ...e, common: {}, general: null }));
  };

  const changeRow = (key, patch) => {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    setErrors((e) => {
      if (!e.rows[key]) return e;
      const rowErr = { ...e.rows[key] };
      Object.keys(patch).forEach((f) => delete rowErr[f]);
      return { ...e, rows: { ...e.rows, [key]: rowErr } };
    });
  };

  // Incolla una colonna da Excel: un valore per riga, aggiungendo righe se servono
  const pasteColumn = (startKey, field, e) => {
    const text = e.clipboardData?.getData('text');
    if (!text) return;
    const values = text.replace(/\r\n?/g, '\n').split('\n').map((l) => (l.split('\t')[0] || '').trim());
    while (values.length > 1 && values[values.length - 1] === '') values.pop();
    if (values.length <= 1) return;
    e.preventDefault();
    setRows((rs) => {
      const start = rs.findIndex((r) => r.key === startKey);
      const next = rs.map((r) => ({ ...r }));
      while (next.length < start + values.length) next.push(newRow(prefix));
      values.forEach((v, i) => {
        let val = v;
        if (field === 'ticket' && prefix && val && !val.startsWith(prefix)) val = prefix + val;
        next[start + i][field] = val;
      });
      return next;
    });
  };

  const save = async (candidates) => {
    setSaving(true);
    const savedKeys = new Set();
    try {
      for (const r of candidates) {
        const ticket = r.ticket.trim() === prefix ? '' : cleanTicketId(r.ticket, project?.ticket_id_prefix);
        await createWorklog({
          user_id: userId,
          project_id: common.projectId,
          activity_id: common.activityId,
          status_id: '1',
          effort_type_id: common.effortTypeId,
          effort: parseEffort(r.effort).minutes,
          ticket_id: ticket,
          description: r.description.trim(),
          date: dateKey,
        });
        savedKeys.add(r.key);
      }
      setRows([newRow(prefix)]);
      setErrors(noErrors());
      setSnack(`${savedKeys.size} worklog${savedKeys.size === 1 ? '' : 's'} saved`);
    } catch (err) {
      setRows((rs) => {
        const remaining = rs.filter((r) => !savedKeys.has(r.key));
        return remaining.length ? remaining : [newRow(prefix)];
      });
      setErrors({
        ...noErrors(),
        general: savedKeys.size ? `Saved ${savedKeys.size} of ${candidates.length}. The other rows weren’t saved: ${err.message}` : `Not saved: ${err.message}`,
      });
    } finally {
      setSaving(false);
      if (savedKeys.size) refresh();
    }
  };

  const handleSave = () => {
    if (saving || locked) return;
    const candidates = rows.filter((r) => r.effort.trim() || r.description.trim() || (r.ticket.trim() && r.ticket.trim() !== prefix));
    const next = noErrors();
    if (!candidates.length) {
      next.general = 'Fill in at least one row.';
      setErrors(next);
      return;
    }
    candidates.forEach((r) => {
      const e = validateWorklog({ project, activity, effortTypeId: common.effortTypeId, ...r }, prefix, date);
      const { project: pe, activity: ae, effortTypeId: te, ...rowErr } = e;
      if (pe) next.common.project = pe;
      if (ae) next.common.activity = ae;
      if (te) next.common.effortTypeId = te;
      if (Object.keys(rowErr).length) next.rows[r.key] = rowErr;
    });
    const newMinutes = candidates.reduce((sum, r) => sum + (parseEffort(r.effort).minutes || 0), 0);
    if (savedMinutes + newMinutes > MAX_DAILY_MINUTES) next.general = 'The daily total can’t exceed 24 hours.';
    setErrors(next);
    if (Object.keys(next.common).length || Object.keys(next.rows).length || next.general) return;
    if (savedMinutes + newMinutes > DAILY_WARNING_MINUTES) {
      setConfirmOver({ candidates, total: savedMinutes + newMinutes });
      return;
    }
    save(candidates);
  };

  // ---- Modifica ----
  const startEdit = (w) => {
    ensureAllowed(w.project_id);
    setEditErrors({});
    setEditing({
      worklog: w,
      value: {
        projectId: w.project_id,
        activityId: w.activity_id,
        effortTypeId: w.effort_type_id,
        ticket: String(w.ticket_id || '').trim(),
        effort: String(w.effort ?? ''),
        description: w.description || '',
      },
    });
  };
  const editProject = editing ? findProject(editing.value.projectId) : null;
  const editActivity = editing ? findActivity(editing.value.activityId) : null;
  const editPrefix = getTicketPrefix(editProject, editActivity);
  const changeEdit = (patch) => {
    if (patch.projectId) ensureAllowed(patch.projectId);
    setEditing((ed) => {
      const value = { ...ed.value, ...patch };
      const newPrefix = getTicketPrefix(findProject(value.projectId), findActivity(value.activityId));
      if (('projectId' in patch || 'activityId' in patch) && newPrefix && !value.ticket.startsWith(newPrefix)) value.ticket = newPrefix + value.ticket;
      return { ...ed, value };
    });
    setEditErrors({});
  };
  const saveEdit = async () => {
    const v = editing.value;
    const e = validateWorklog({ project: editProject, activity: editActivity, ...v }, editPrefix, date);
    const others = savedMinutes - (Number(editing.worklog.effort) || 0);
    if (!e.effort && others + parseEffort(v.effort).minutes > MAX_DAILY_MINUTES) e.effort = 'The daily total would exceed 24h';
    setEditErrors(e);
    if (Object.keys(e).length) return;
    setEditSaving(true);
    try {
      await updateWorklog({
        id: editing.worklog.id,
        user_id: userId,
        project_id: v.projectId,
        activity_id: v.activityId,
        status_id: editing.worklog.status_id || '1',
        effort_type_id: v.effortTypeId,
        effort: parseEffort(v.effort).minutes,
        ticket_id: v.ticket.trim() === editPrefix ? '' : cleanTicketId(v.ticket, editProject?.ticket_id_prefix),
        description: v.description.trim(),
        date: dateKey,
      });
      setEditing(null);
      setSnack('Worklog updated');
      refresh();
    } catch (err) {
      setEditErrors({ general: `Not saved: ${err.message}` });
    } finally {
      setEditSaving(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteWorklog(toDelete.id);
      setToDelete(null);
      setSnack('Worklog deleted');
      refresh();
    } catch (err) {
      setSnack(`Delete failed: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // ---- Assenze ----
  const setAbsence = async (fullDay) => {
    await addAbsences(userId, [{ date: dateKey, full_day: fullDay }]);
    refresh();
  };
  const removeAbsence = async () => {
    if (absence) await deleteAbsence(absence.id);
    refresh();
  };
  // Assenze su più giorni (anche in altri mesi): si svuota la cache di ogni mese toccato
  const absenceDaysSaved = ({ months, complete, message }) => {
    if (userId) months.forEach((ym) => invalidatePeopleMonth([userId], ym));
    setRefreshToken((t) => t + 1);
    if (complete) {
      setAbsenceDaysOpen(false);
      setSnack(message);
    }
  };

  if (!selectedUser) return <Alert severity="info">Waiting for authentication to load...</Alert>;

  const editProjects =
    editing && !projectsForDate.some((p) => p.id === editing.value.projectId) ? [...projectsForDate, editProject].filter(Boolean) : projectsForDate;

  return (
    <PageContainer>
      <PageHeader title="Track hours">
        <DayNavigator date={date} onChange={changeDate} />
        <AbsenceMenu absence={absence} disabled={!userId} onSet={setAbsence} onRemove={removeAbsence} onOpenDays={() => setAbsenceDaysOpen(true)} />
      </PageHeader>

      {optionsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {optionsError}
        </Alert>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {absence?.full_day && <Alert severity="info">You’re marked absent for the whole day.</Alert>}
          {locked ? (
            <Alert severity="info">{lockMessage}</Alert>
          ) : (
            <EntryCard
              common={common}
              rows={rows}
              errors={errors}
              onCommonChange={changeCommon}
              onRowChange={changeRow}
              onAddRow={() => setRows((rs) => [...rs, newRow(prefix)])}
              onRemoveRow={(key) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.key !== key) : rs))}
              onPaste={pasteColumn}
              onSave={handleSave}
              saving={saving}
              projects={projectsForDate}
              activities={activitiesFor(common.projectId)}
              effortTypes={effortTypes}
              project={project}
              activity={activity}
              prefix={prefix}
              blockMessage={blockMessage}
              savedMinutes={savedMinutes}
              pendingMinutes={pendingMinutes}
              dayLabel={date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
            />
          )}
          <DayWorklogTable
            worklogs={worklogs}
            loading={dayLoading}
            error={dayError}
            dayLabel={dayLabel}
            locked={locked}
            lockMessage={lockMessage}
            onEdit={startEdit}
            onDelete={setToDelete}
          />
        </Grid>
        <Grid item xs={12} lg={4} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <MonthCalendar
            stats={month}
            title={`${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`}
            selectedKey={dateKey}
            onOpenDay={(key) => changeDate(parseLocalDate(key))}
          />
          <DayProjectsCard worklogs={worklogs} />
        </Grid>
      </Grid>

      <WorklogEditDialog
        open={!!editing}
        value={editing?.value}
        errors={editErrors}
        onChange={changeEdit}
        onClose={() => setEditing(null)}
        onSave={saveEdit}
        saving={editSaving}
        projects={editProjects}
        activities={activitiesFor(editing?.value.projectId)}
        effortTypes={effortTypes}
        project={editProject}
        activity={editActivity}
        prefix={editPrefix}
        dayLabel={dayLabel}
      />

      <Dialog open={!!confirmOver} onClose={() => setConfirmOver(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>More than 8 hours</DialogTitle>
        <DialogContent>
          <Typography>
            The total for {dayLabel} will be <strong>{confirmOver && formatMinutes(confirmOver.total)}</strong>. Save anyway?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setConfirmOver(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              const { candidates } = confirmOver;
              setConfirmOver(null);
              save(candidates);
            }}
            sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
          >
            Save anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Delete worklog?</DialogTitle>
        <DialogContent>
          {toDelete && (
            <Typography>
              {formatMinutes(toDelete.effort)} on <strong>{toDelete.project_name}</strong> · {toDelete.activity_name}
              {toDelete.description ? ` — ${toDelete.description}` : ''}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setToDelete(null)} disabled={deleting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <AbsenceDaysDialog open={absenceDaysOpen} userId={userId} initialDate={date} onClose={() => setAbsenceDaysOpen(false)} onSaved={absenceDaysSaved} />

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </PageContainer>
  );
}
